import { Capacitor } from "@capacitor/core";
import * as SentryReact from "@sentry/react";
import * as SentryCapacitor from "@sentry/capacitor";

type ClientPlatform = "web" | "ios" | "android";

export type ClientSentryEnv = {
  MODE: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_SENTRY_DSN?: string;
};

const TRACES_SAMPLE_RATE = 0.1;

function resolveClientPlatform(): ClientPlatform {
  if (!Capacitor.isNativePlatform()) return "web";
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return "ios";
  if (platform === "android") return "android";
  return "web";
}

function resolveDsn(env: ClientSentryEnv): string | undefined {
  return env.VITE_SENTRY_DSN?.trim();
}

function resolveSentryEnvironment(env: ClientSentryEnv): string {
  return (env.VITE_SENTRY_ENVIRONMENT || env.MODE).trim();
}

function resolveTraceSampleRate(env: ClientSentryEnv): number {
  const raw = env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  if (!raw) return TRACES_SAMPLE_RATE;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : TRACES_SAMPLE_RATE;
}

export function initSentryForClient(
  env: ClientSentryEnv = import.meta.env as unknown as ClientSentryEnv,
): void {
  const platform = resolveClientPlatform();
  const dsn = resolveDsn(env);

  if (!dsn) {
    const message = "Missing required env: VITE_SENTRY_DSN";
    if (env.MODE === "production") {
      throw new Error(message);
    }
    console.warn(`[sentry] ${message}. Sentry client initialization skipped.`);
    return;
  }

  const options = {
    dsn,
    environment: resolveSentryEnvironment(env),
    tracesSampleRate: resolveTraceSampleRate(env),
  };

  if (platform === "ios" || platform === "android") {
    SentryCapacitor.init(options, SentryReact.init);
    return;
  }

  SentryReact.init({
    ...options,
    integrations: [
      SentryReact.browserTracingIntegration(),
      SentryReact.replayIntegration(),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
